function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Suspense, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { OverlayProvider, RootThemeProvider, useStateInRef } from 'seagull';
import { MediaQueriesProvider } from 'seagull/MediaQueries';
import { isTestMode } from 'seagull/utils/test-mode';
import useEventTargets from "./event-reporter/event-targets/useEventTargets";
import { Observable } from "./infra/Observable/Observable";
import { BillingObservableProvider } from "./providers/BillingObservableProvider";
import { LocationProvider } from "./providers/LocationProvider";
import { BiTrackingProvider } from "./components/BiTrackingProvider/BiTrackingProvider";
import ErrorBoundaryPage from "./components/ErrorBoundaryPage";
import LoadingIndicator from "./components/LoadingIndicator";
import { defaultTheme } from "./constants";
import { useIoContext, useStore, useStoreActions } from "./contexts";
import { EventReporterProvider } from "./event-reporter";
import { useManageLocationStack } from "./hooks/useManageLocationStack";
import { getIntentBehavior } from "./infra/access-control";
import "./infra/styles.scss";
import "./infra/tailwind.css";
import { IntercomStarter } from "./intercom/IntercomStarter";
import Main from "./pages/Main/Main";
import AuthProvider from "./providers/AuthProvider";
import { InvoiceChangesObservableProvider } from "./providers/InvoiceChangesObservableProvider";
import { ModalCloseManagerProvider } from "./providers/ModalCloseManager";
import SeagullIntentBehaviorProvider from "./providers/SeagullIntentBehaviorProvider";
import { PusherBusinessChannelProvider } from "./pusher";
import { selectors } from "./store/state-selectors";
import { jsx as _jsx } from "react/jsx-runtime";
function loadAppcues(appcuesAccountId) {
  const appcuesScriptId = 'appcues-bootstrap';
  return new Promise(resolve => {
    if (document.getElementById(appcuesScriptId)) {
      resolve();
    } else {
      window.AppcuesSettings = {
        enableURLDetection: true
      };
      window.appcuesAccountId = appcuesAccountId;
      const script = document.createElement('script');
      script.id = appcuesScriptId;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.type = 'text/javascript';
      script.src = 'https://cdn.anchor.sh/appcues/appcues-bootstrap.js';
      script.integrity = 'sha384-0WU2TUOJTyy11Iqf6ViiHLuqyhmagqyTyHnyxmMoRnKHAIK/wef8Y/KqAbvIFHaY';
      script.onload = () => resolve();
      document.head.appendChild(script);
    }
  });
}

/** Wraps all modals with an error boundary modal. */
const ModalWrapper = props => /*#__PURE__*/_jsx(ErrorBoundaryPage, _objectSpread({
  isModal: true
}, props));
export function App() {
  const {
    data
  } = useStore(selectors.ff.defaultFeatureFlags);
  const location = useLocation();
  const {
    search
  } = location;
  const getStateInRef = useStateInRef({
    search
  });
  const {
    setAuthToken
  } = useStoreActions();
  const {
    cookies
  } = useIoContext();
  useManageLocationStack({
    initialLocations: [location]
  });
  const hasDefaultFeatureFlags = !!data;
  useEffect(() => {
    if (!hasDefaultFeatureFlags) return;
    const {
      search
    } = getStateInRef();

    // Override feature-flags from URL.
    const featureFlags = new URLSearchParams(search).get('ff');
    cookies.setCookie('ff', featureFlags || '');
    const authToken = cookies.getAuthToken();
    setAuthToken(authToken);
  }, [hasDefaultFeatureFlags]);
  const visitorSyncFrame = useMemo(() => {
    var _document$getElementB;
    return (_document$getElementB = document.getElementById('visitor-sync-frame')) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.contentWindow;
  }, []);
  const billingObservable = useMemo(() => new Observable(), []);
  const invoiceChangesObservable = useMemo(() => new Observable(), []);
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        cacheTime: 10 * 60 * 1000
      }
    }
  }), []);
  return /*#__PURE__*/_jsx(QueryClientProvider, {
    client: queryClient,
    children: /*#__PURE__*/_jsx(SeagullIntentBehaviorProvider, {
      getIntentBehavior: getIntentBehavior,
      children: /*#__PURE__*/_jsx(ErrorBoundaryPage, {
        children: /*#__PURE__*/_jsx(Suspense, {
          fallback: /*#__PURE__*/_jsx(LoadingIndicator, {}),
          children: /*#__PURE__*/_jsx(MediaQueriesProvider, {
            children: /*#__PURE__*/_jsx(LocationProvider, {
              location: window.location,
              children: /*#__PURE__*/_jsx(AuthProvider, {
                loadAppcues: loadAppcues,
                visitorSyncFrame: visitorSyncFrame,
                visitorSyncOrigin: window._visitorSyncHostname || '',
                children: /*#__PURE__*/_jsx(IntercomStarter, {
                  children: /*#__PURE__*/_jsx(PusherBusinessChannelProvider, {
                    children: /*#__PURE__*/_jsx(BillingObservableProvider, {
                      observable: billingObservable,
                      children: /*#__PURE__*/_jsx(InvoiceChangesObservableProvider, {
                        observable: invoiceChangesObservable,
                        children: /*#__PURE__*/_jsx(OverlayProvider, {
                          disableEnforceFocus: true,
                          ModalWrapper: ModalWrapper,
                          isTestMode: isTestMode,
                          children: /*#__PURE__*/_jsx(ModalCloseManagerProvider, {
                            children: /*#__PURE__*/_jsx(RootThemeProvider, {
                              theme: defaultTheme,
                              children: /*#__PURE__*/_jsx(BiTrackingProvider, {
                                children: /*#__PURE__*/_jsx(Main, {})
                              })
                            })
                          })
                        })
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  });
}
export function AppWithEventReporter() {
  return /*#__PURE__*/_jsx(EventReporterProvider, {
    eventTargets: useEventTargets(),
    children: /*#__PURE__*/_jsx(App, {})
  });
}